import { Component, inject, input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroSelectComponent, ZiroSelectConfig } from '../../../../forms/fields/select/ziro-select.component';
import {
  ZiroFieldComponentConfig,
  ZiroFormConfig,
  ZiroFormsValidationState,
} from '../../../../forms/ziro-forms-models';
import {
  CustomCheckboxFieldConfig,
  CustomMultiSelectFieldConfig,
  SelectFieldConfig,
  TextFieldConfig,
} from '../../../../shared/models/generated/ziroModels';
import { Observable, of, Subscription } from 'rxjs';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { TranslateService } from '@ngx-translate/core';

export interface DnDetailsFormEntity {
  description: string;
  urgentPriority: boolean;
  classOfService: string;
  alertingName: string;
  routePartition: string;
  callPickupGroupName: string;
  voicemailProfile: string;
  lineGroups: string[];
  autoAnswer: string;
}

export interface DnDetailsFieldConfig {
  description: TextFieldConfig;
  urgentPriority: CustomCheckboxFieldConfig;
  classOfService: SelectFieldConfig<string>;
  alertingName: TextFieldConfig;
  routePartition: SelectFieldConfig<string>;
  callPickupGroupName: SelectFieldConfig<string>;
  voicemailProfile: SelectFieldConfig<string>;
  lineGroups: CustomMultiSelectFieldConfig;
  autoAnswer: SelectFieldConfig<string>;
}

@Component({
  selector: 'ziro-directory-number-details-form',
  templateUrl: './directory-number-details-form.component.html',
  standalone: false,
})
export class ZiroDirectoryNumberDetailsFormComponent
  extends ZiroFormAbstractDirective<DnDetailsFormEntity>
  implements OnInit, OnChanges, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _translateService = inject(TranslateService);

  readonly fieldConfig = input<DnDetailsFieldConfig>();
  formConfig: ZiroFormConfig;
  private _subscribers = new Subscription();

  ngOnInit(): void {
    const ziroFormsUpdate = this.ziroFormsUpdate$.subscribe(() => {
      if (this.fieldComponents.length) {
        this.reevaluateFields();
      }
    });

    this._subscribers.add(ziroFormsUpdate);

    this._initFormConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.fieldConfig?.currentValue && this.fieldComponents?.length) {
      setTimeout(() => {
        this.reevaluateFields();
      });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscribers.unsubscribe();
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        description: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.extensionDescription.label',
          dataAutomation: 'dn-detail-description',
          hidden: () => !this.fieldConfig().description.show,
          required: () => this.fieldConfig().description.required,
          defaultValue: () => this.fieldConfig().description.defaultValue,
          validation: [
            {
              validator: (val: string) => {
                return val.length > 50 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: this._translateService.instant('tkey;validators.global.error.maxlength', { maxlength: 50 }),
            },
          ],
        },
        urgentPriority: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.urgent_priority.label',
          dataAutomation: 'dn-detail-urgent-priority',
          hidden: () => !this.fieldConfig().urgentPriority.show,
          defaultValue: () => this.fieldConfig().urgentPriority.defaultValue,
        },
        classOfService: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.classOfService.label',
          dataAutomation: 'dn-detail-class-of-service',
          componentConfig: new ZiroSelectConfig({ options: this.fieldConfig().classOfService.possibleOptions }),
          hidden: () => !this.fieldConfig().classOfService.show,
          required: () => this.fieldConfig().classOfService.required,
          defaultValue: () => this.fieldConfig().classOfService.defaultValue,
        },
        alertingName: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.alertingName.label',
          dataAutomation: 'dn-detail-alerting-name',
          hidden: () => !this.fieldConfig().alertingName.show,
          required: () => this.fieldConfig().alertingName.required,
          defaultValue: () => this.fieldConfig().alertingName.defaultValue,
          validation: [
            {
              validator: (val) =>
                !val || val.length <= 50 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: {
                content: 'tkey;global.validation.max_length',
                params: {
                  maxLength: '50',
                },
              },
            },
          ],
        },
        routePartition: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.route_partition.label',
          dataAutomation: 'dn-detail-route-partition',
          componentConfig: new ZiroSelectConfig({ options: this.fieldConfig().routePartition.possibleOptions }),
          hidden: () => !this.fieldConfig().routePartition.show,
          required: () => this.fieldConfig().routePartition.required,
          defaultValue: () => this.fieldConfig().routePartition.defaultValue,
        },
        callPickupGroupName: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.callPickupGroup.label',
          dataAutomation: 'dn-detail-call-pickup-group-name',
          componentConfig: new ZiroSelectConfig({ options: this.fieldConfig().callPickupGroupName.possibleOptions }),
          hidden: () => !this.fieldConfig().callPickupGroupName.show,
          required: () => this.fieldConfig().callPickupGroupName.required,
          defaultValue: () => this.fieldConfig().callPickupGroupName.defaultValue,
        },
        voicemailProfile: {
          label: 'tkey;shared.dnWizard.dnDetails.dnVoicemail.voicemailProfile.label',
          dataAutomation: 'dn-detail-voicemail-profile',
          componentConfig: new ZiroSelectConfig({ options: this.fieldConfig().voicemailProfile.possibleOptions }),
          hidden: () => !this.fieldConfig().voicemailProfile.show,
          required: () => this.fieldConfig().voicemailProfile.required,
          defaultValue: () => this.fieldConfig().voicemailProfile.defaultValue,
        },
        lineGroups: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.associated_line_groups.label',
          dataAutomation: 'dn-detail-line-groups',
          componentConfig: new ZiroSelectConfig({
            options: this.fieldConfig().lineGroups.possibleOptions,
            isMultiSelect: true,
          }),
          hidden: () => !this.fieldConfig().lineGroups.show,
          required: () => this.fieldConfig().lineGroups.required,
          defaultValue: () => this.fieldConfig().lineGroups.defaultValues,
        },
        autoAnswer: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.auto_answer.label',
          dataAutomation: 'dn-detail-auto-answer',
          componentConfig: new ZiroSelectConfig({ options: this.fieldConfig().autoAnswer.possibleOptions }),
          hidden: () => !this.fieldConfig().autoAnswer.show,
          required: () => this.fieldConfig().autoAnswer.required,
          defaultValue: () => this.fieldConfig().autoAnswer.defaultValue,
        },
      },
    };
  }

  reevaluateFields(): void {
    const fieldComponents = new Map(
      this.fieldComponents.map((fieldComponent) => [fieldComponent.fieldId(), fieldComponent]),
    );
    fieldComponents.forEach((fieldComponent: ZiroSelectComponent, fieldId: keyof DnDetailsFieldConfig) => {
      const config: ZiroFieldComponentConfig = fieldComponent.config.componentConfig;

      if (config instanceof ZiroSelectConfig) {
        if (fieldId === 'lineGroups') {
          fieldComponent.applyComponentConfig(
            new ZiroSelectConfig({
              options: this.fieldConfig().lineGroups.possibleOptions,
              isMultiSelect: true,
            }),
          );
        } else {
          fieldComponent.applyComponentConfig(
            new ZiroSelectConfig({
              options: (this.fieldConfig()[fieldId] as SelectFieldConfig<string>).possibleOptions,
            }),
          );
        }
      }
      this.fieldChannels[fieldId].validateSource.next();
    });
  }
}
